<template>
  <Teleport to="body">
    <div v-if="isVisible" class="step-up-overlay">
      <div class="step-up-overlay__backdrop" />
      <div class="step-up-overlay__container">
        <button type="button" class="step-up-overlay__close-button" aria-label="Close" @click="handleCloseWithError">
          <span class="step-up-overlay__close-icon">&times;</span>
        </button>
        <div v-if="isTimedOut" class="step-up-overlay__timeout-message" role="alert" aria-live="polite">
          <p>{{ $t("shared.checkout.step_up_authentication.timeout_message") }}</p>
        </div>
        <iframe ref="iframeRef" title="step_up_authentication" name="step_up_iframe" class="step-up-overlay__iframe" />
        <form ref="formRef" name="stepup" method="POST" :action="stepUpUrl" target="step_up_iframe">
          <input type="hidden" name="JWT" :value="accessToken" />
        </form>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick } from "vue";
import { logClientInformation } from "@/core/api/graphql";
import { Logger } from "@/core/utilities";
import { useUser } from "@/shared/account";
import { submitFormWithRetry } from "@/shared/checkout/utils/submit-form-with-retry";
import { eventBus } from "@/shared/thorlabs/shared";

const STEP_UP_TIMEOUT_MS = 15_000;
const FORM_SUBMIT_MAX_RETRIES = 3;
const FORM_SUBMIT_RETRY_DELAY_MS = 100;

const { user } = useUser();

const isVisible = ref(false);
const isTimedOut = ref(false);
const accessToken = ref("");
const stepUpUrl = ref("");
const authenticationTransactionId = ref<string | undefined>(undefined);
const iframeRef = ref<HTMLIFrameElement | null>(null);
const formRef = ref<HTMLFormElement | null>(null);

let timeoutTimer: ReturnType<typeof setTimeout> | null = null;

const getUserId = (): string => {
  return user.value?.memberId ?? user.value?.id ?? "unknown";
};

const log = (title: string, content: string): void => {
  void logClientInformation({
    title,
    content,
    withTelemetry: true,
    userId: getUserId(),
  }).catch(() => {
    // Logging failure must not affect the 3DS flow
  });
};

const clearTimeoutTimer = () => {
  if (timeoutTimer !== null) {
    clearTimeout(timeoutTimer);
    timeoutTimer = null;
  }
};

const handleIframeLoad = () => {
  clearTimeoutTimer();
  isTimedOut.value = false;
  Logger.debug("[StepUpAuthentication] Iframe loaded — timeout cancelled");
};

const startTimeoutTimer = () => {
  timeoutTimer = setTimeout(() => {
    isTimedOut.value = true;
    Logger.warn("[StepUpAuthentication] Timeout reached — iframe did not load within " + STEP_UP_TIMEOUT_MS + "ms");
    log(
      "StepUpAuthentication.timeout",
      `Iframe did not load within ${STEP_UP_TIMEOUT_MS}ms. authenticationTransactionId=${authenticationTransactionId.value ?? "unknown"}`,
    );
  }, STEP_UP_TIMEOUT_MS);
};

const showOverlay = async (payload: {
  accessToken: string;
  stepUpUrl: string;
  authenticationTransactionId?: string;
}) => {
  accessToken.value = payload.accessToken;
  stepUpUrl.value = payload.stepUpUrl;
  authenticationTransactionId.value = payload.authenticationTransactionId;
  isVisible.value = true;
  isTimedOut.value = false;

  await nextTick();

  // Attach iframe load listener
  if (iframeRef.value) {
    iframeRef.value.addEventListener("load", handleIframeLoad);
  }

  // Start timeout timer
  startTimeoutTimer();

  // Submit form with retry
  const submitted = await submitFormWithRetry({
    formRef,
    maxRetries: FORM_SUBMIT_MAX_RETRIES,
    retryDelayMs: FORM_SUBMIT_RETRY_DELAY_MS,
    onRetrySuccess: (attempt) => {
      Logger.info(`[StepUpAuthentication] Form submitted on retry attempt ${attempt}`);
      log(
        "StepUpAuthentication.formSubmitRetried",
        `Form submitted on attempt ${attempt}. authenticationTransactionId=${authenticationTransactionId.value ?? "unknown"}`,
      );
    },
  });

  if (submitted) {
    const stepUpDomain = (() => {
      try {
        return new URL(payload.stepUpUrl).hostname;
      } catch {
        return "unknown";
      }
    })();
    log(
      "StepUpAuthentication.formSubmitted",
      `Form submitted to ${stepUpDomain}. authenticationTransactionId=${authenticationTransactionId.value ?? "unknown"}`,
    );
  } else {
    Logger.error("[StepUpAuthentication] Form submit failed after all retries — formRef is null");
    log(
      "StepUpAuthentication.formSubmitFailed",
      `Form ref unavailable after ${FORM_SUBMIT_MAX_RETRIES} retries. authenticationTransactionId=${authenticationTransactionId.value ?? "unknown"}`,
    );
    closeOverlay();
    eventBus.emit("paymentError", {
      reason: "AUTHENTICATION_VALIDATION_FAILED",
    });
  }
};

const cleanup = () => {
  clearTimeoutTimer();
  if (iframeRef.value) {
    iframeRef.value.removeEventListener("load", handleIframeLoad);
  }
};

const closeOverlay = () => {
  cleanup();
  isVisible.value = false;
  isTimedOut.value = false;
  accessToken.value = "";
  stepUpUrl.value = "";
  authenticationTransactionId.value = undefined;
};

const handleCloseWithError = () => {
  const cause = isTimedOut.value ? "timeout" : "user_closed";
  log(
    "StepUpAuthentication.closedWithoutCompletion",
    `Overlay closed without 3DS completion. cause=${cause}, authenticationTransactionId=${authenticationTransactionId.value ?? "unknown"}`,
  );
  eventBus.emit("closeStepUpAuthentication");
  eventBus.emit("paymentError", {
    reason: "AUTHENTICATION_VALIDATION_FAILED",
  });
};

onMounted(() => {
  eventBus.on("showStepUpAuthentication", showOverlay);
  eventBus.on("closeStepUpAuthentication", closeOverlay);
});

onBeforeUnmount(() => {
  cleanup();
  eventBus.off("showStepUpAuthentication", showOverlay);
  eventBus.off("closeStepUpAuthentication", closeOverlay);
});
</script>

<style lang="scss" scoped>
.step-up-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;

  &__backdrop {
    position: absolute;
    inset: 0;
    background-color: rgb(0 0 0 / 50%);
  }

  &__container {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 450px;
    max-width: 95vw;
    height: 500px;
    max-height: 90vh;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 24px rgb(0 0 0 / 20%);
  }

  &__iframe {
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 8px;
  }

  &__close-button {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background-color: transparent;
    border: none;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: rgb(0 0 0 / 5%);
    }

    &:active {
      background-color: rgb(0 0 0 / 10%);
    }
  }

  &__close-icon {
    font-size: 24px;
    font-weight: 300;
    line-height: 1;
    color: #666;
  }

  &__timeout-message {
    position: absolute;
    inset: 0;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px;
    background-color: rgb(255 255 255 / 95%);
    border-radius: 8px;

    p {
      font-size: 14px;
      line-height: 1.5;
      color: #333;
      text-align: center;
    }
  }
}
</style>
